import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { authApi } from '@/services/api';
import {
  authPageStyles,
  authTextStyles,
  authAlertStyles,
  authVisualStyles,
  authButtonStyles,
} from '@/styles/authPageStyles';
import { CheckademicLogo } from '@/components/CheckademicLogo';
import { SchoolLogo } from '@/components/SchoolLogo';
import { LanguageToggle } from '@/components/parent/LanguageToggle';
import { useAuthStore } from '@/stores/authStore';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function RequestPasswordReset() {
  const { t } = useTranslation('common');
  usePageTitle(t('auth.requestReset.pageTitle'));
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { tenant } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Extract subdomain from hostname
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const subdomain = parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'admin' ? parts[0] : null;

    if (!subdomain) {
      setError(t('auth.requestReset.schoolNotFound'));
      setLoading(false);
      return;
    }

    try {
      await authApi.requestPasswordReset(email, subdomain);

      setSuccess(true);
    } catch {
      setError(t('auth.requestReset.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          <LanguageToggle />
          {/* School Logo (if uploaded) */}
          {tenant?.settings.branding.logoFileId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <CheckademicLogo
              primaryColor="var(--primary-color)"
              accentColor="var(--secondary-color)"
              textColor="var(--primary-color)"
            />
          </div>
          <div style={authVisualStyles.successEmoji}>
            <Mail size={48} aria-hidden="true" />
          </div>
          <h1 style={authTextStyles.titleCentered}>{t('auth.requestReset.checkEmail')}</h1>
          <p style={authTextStyles.descriptionSmall}>
            <Trans t={t} i18nKey="auth.requestReset.emailSentInfo" values={{ email }} components={{ strong: <strong /> }} />
          </p>
          <p style={authTextStyles.description}>{t('auth.requestReset.linkExpiry')}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/login')} style={authButtonStyles.fullWidth}>
            {t('auth.requestReset.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={authPageStyles.pageContainer}>
      <div style={authPageStyles.cardContainer}>
        <LanguageToggle />
        {/* School Logo (if uploaded) */}
        {tenant?.settings.branding.logoFileId && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <CheckademicLogo
            primaryColor="var(--primary-color)"
            accentColor="var(--secondary-color)"
            textColor="var(--primary-color)"
          />
        </div>
        <h1 style={authTextStyles.title}>{t('auth.requestReset.title')}</h1>
        <p style={authTextStyles.description}>
          {t('auth.requestReset.description')}
        </p>

        {error && <div style={authAlertStyles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              {t('form.emailAddress')} <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <input
              id="email"
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoFocus
              placeholder={t('auth.requestReset.emailPlaceholder')}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={authButtonStyles.fullWidthWithMargin}
            disabled={loading}
          >
            {loading ? t('auth.requestReset.sending') : t('auth.requestReset.sendResetLink')}
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/login')}
            style={authButtonStyles.fullWidth}
            disabled={loading}
          >
            {t('auth.requestReset.backToLogin')}
          </button>
        </form>
      </div>
    </div>
  );
}
